/**
 * TEXT PRIMITIVES for the share PDF — real, selectable text wherever the
 * built-in Helvetica can print it, a browser-rendered image where it cannot.
 *
 * jsPDF's standard fonts are WinAnsi only: a ₹, a "→", a Malayalam or Arabic
 * name would print as garbage. Rather than ship a font per script (and jsPDF
 * does no Indic shaping anyway), every string is tested with `winAnsiSafe`;
 * the rare non-Latin one is drawn by the browser onto a transparent canvas
 * and placed as a small PNG at the same size, so the page reads the same.
 *
 * Measurement goes through the same split: vector text is measured by jsPDF,
 * raster text by a canvas with the app's font stack. In a non-browser
 * runtime (unit tests) the canvas is absent and raster text degrades to the
 * vector path, so the layout code stays testable headless.
 */
import type { jsPDF } from "jspdf";

export type Rgb = [number, number, number];

export const INK: Rgb = [12, 33, 27]; // #0c211b
export const MUTED: Rgb = [86, 98, 94]; // #56625e
export const FAINT: Rgb = [138, 147, 143]; // #8a938f
export const RULE: Rgb = [214, 235, 227]; // #d6ebe3

export const PT_MM = 25.4 / 72;
/** jsPDF's and jspdf-autotable's default line height factor. */
export const LINE_FACTOR = 1.15;
export const lineHeightMm = (pt: number): number => pt * PT_MM * LINE_FACTOR;

const FONT_STACK = '"Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif';
/** Raster text: 10 px per mm ≈ 254 dpi. */
const PX_PER_MM = 10;

// The 27 code points above U+00FF that WinAnsiEncoding (and jsPDF's standard
// font encoder) carries: € ‚ ƒ „ … † ‡ ˆ ‰ Š ‹ Œ Ž ‘ ’ “ ” • – — ˜ ™ š › œ ž Ÿ
const WIN_ANSI_EXTRA = "€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ";

/** True when jsPDF's built-in Helvetica can print every character. */
export function winAnsiSafe(text: string): boolean {
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code > 0xff && !WIN_ANSI_EXTRA.includes(ch)) return false;
  }
  return true;
}

const hasDom = (): boolean => typeof document !== "undefined" && typeof document.createElement === "function";

let measureCtx: CanvasRenderingContext2D | null | undefined;
function canvasCtx(): CanvasRenderingContext2D | null {
  if (measureCtx === undefined) {
    try {
      measureCtx = hasDom() ? document.createElement("canvas").getContext("2d") : null;
    } catch {
      measureCtx = null;
    }
  }
  return measureCtx;
}

const canvasFont = (pt: number, bold: boolean) => `${bold ? 600 : 400} ${pt * PT_MM * PX_PER_MM}px ${FONT_STACK}`;

export function setFont(doc: jsPDF, pt: number, bold = false, color: Rgb = INK): void {
  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(pt);
  doc.setTextColor(color[0], color[1], color[2]);
}

/** Width of one line of text in mm, by whichever engine will draw it. */
export function textWidthMm(doc: jsPDF, text: string, pt: number, bold = false): number {
  if (!text) return 0;
  if (!winAnsiSafe(text)) {
    const ctx = canvasCtx();
    if (ctx) {
      ctx.font = canvasFont(pt, bold);
      return ctx.measureText(text).width / PX_PER_MM;
    }
  }
  setFont(doc, pt, bold);
  return doc.getTextWidth(text);
}

/** The widest single word of a (possibly multi-line) text, in mm. */
export function longestWordMm(doc: jsPDF, text: string, pt: number, bold = false): number {
  let widest = 0;
  for (const word of text.split(/\s+/)) {
    if (!word) continue;
    widest = Math.max(widest, textWidthMm(doc, word, pt, bold));
  }
  return widest;
}

/** Word-wrap raster text with the canvas's own metrics (a word wider than
 *  the box is broken by character, like jsPDF does). */
function rasterWrap(ctx: CanvasRenderingContext2D, line: string, maxPx: number): string[] {
  const out: string[] = [];
  let current = "";
  const push = () => {
    if (current) out.push(current);
    current = "";
  };
  for (const word of line.split(/\s+/).filter(Boolean)) {
    const next = current ? `${current} ${word}` : word;
    if (ctx.measureText(next).width <= maxPx) {
      current = next;
      continue;
    }
    push();
    if (ctx.measureText(word).width <= maxPx) {
      current = word;
      continue;
    }
    let piece = "";
    for (const ch of word) {
      if (ctx.measureText(piece + ch).width > maxPx && piece) {
        out.push(piece);
        piece = "";
      }
      piece += ch;
    }
    current = piece;
  }
  push();
  return out.length ? out : [""];
}

/** `text` wrapped to `maxMm`, honouring its own line breaks. */
export function wrapText(doc: jsPDF, text: string, pt: number, bold: boolean, maxMm: number): string[] {
  const lines = text.split("\n");
  const out: string[] = [];
  const ctx = !winAnsiSafe(text) ? canvasCtx() : null;
  if (ctx) ctx.font = canvasFont(pt, bold);
  else setFont(doc, pt, bold);
  for (const line of lines) {
    if (!line.trim()) {
      out.push("");
      continue;
    }
    if (ctx) out.push(...rasterWrap(ctx, line, Math.max(1, maxMm * PX_PER_MM)));
    else out.push(...(doc.splitTextToSize(line, Math.max(1, maxMm)) as string[]));
  }
  return out;
}

export interface RasterImage {
  dataUrl: string;
  widthMm: number;
  heightMm: number;
}

/**
 * Lines of text drawn by the browser onto a transparent PNG `widthMm` wide,
 * one `lineHeightMm(pt)` per line — the same metrics jsPDF would have used,
 * so a raster cell sits exactly where a vector one would.
 */
export function rasterLines(
  lines: string[],
  pt: number,
  opts: { bold?: boolean; color?: Rgb; align?: "left" | "right" | "center"; widthMm: number },
): RasterImage | null {
  if (!hasDom()) return null;
  const lh = lineHeightMm(pt);
  const widthPx = Math.max(1, Math.round(opts.widthMm * PX_PER_MM));
  const heightPx = Math.max(1, Math.round(lines.length * lh * PX_PER_MM));
  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.font = canvasFont(pt, opts.bold ?? false);
  const [r, g, b] = opts.color ?? INK;
  ctx.fillStyle = `rgb(${r},${g},${b})`;
  ctx.textBaseline = "top";
  ctx.textAlign = opts.align ?? "left";
  const x = opts.align === "right" ? widthPx : opts.align === "center" ? widthPx / 2 : 0;
  // Centre the glyph box inside the line box, as the vector text sits.
  const inset = ((lh - pt * PT_MM) / 2) * PX_PER_MM;
  lines.forEach((line, i) => ctx.fillText(line, x, i * lh * PX_PER_MM + inset));
  return { dataUrl: canvas.toDataURL("image/png"), widthMm: widthPx / PX_PER_MM, heightMm: heightPx / PX_PER_MM };
}

/** Draw already-wrapped lines at (x, y) — top-left anchored — by the right engine. */
export function drawLines(
  doc: jsPDF,
  lines: string[],
  x: number,
  y: number,
  pt: number,
  opts: { bold?: boolean; color?: Rgb; align?: "left" | "right" | "center"; widthMm: number },
): void {
  if (!lines.length) return;
  const text = lines.join("\n");
  if (!winAnsiSafe(text)) {
    const img = rasterLines(lines, pt, opts);
    if (img) {
      doc.addImage(img.dataUrl, "PNG", x, y, img.widthMm, img.heightMm, undefined, "FAST");
      return;
    }
  }
  setFont(doc, pt, opts.bold ?? false, opts.color ?? INK);
  const align = opts.align ?? "left";
  const ax = align === "right" ? x + opts.widthMm : align === "center" ? x + opts.widthMm / 2 : x;
  doc.text(lines, ax, y, { baseline: "top", align });
}

export interface TextLine {
  text: string;
  pt: number;
  bold?: boolean;
  color?: Rgb;
  /** extra space under the line, mm */
  gapAfter?: number;
}

export interface Flow {
  /** lowest y the content may reach on this page */
  bottom: number;
  /** start a new page; returns the y to continue from */
  newPage: () => number;
}

/**
 * A block of wrapped text lines, flowing across pages. Returns the y under
 * the block. Each line is its own unit, so a long block never leaves a page
 * half empty and never overruns the footer.
 */
export function drawTextBlock(doc: jsPDF, lines: TextLine[], x: number, y: number, width: number, flow: Flow): number {
  for (const line of lines) {
    const wrapped = wrapText(doc, line.text, line.pt, line.bold ?? false, width);
    const lh = lineHeightMm(line.pt);
    let i = 0;
    while (i < wrapped.length) {
      if (y + lh > flow.bottom) y = flow.newPage();
      const room = Math.max(1, Math.floor((flow.bottom - y) / lh));
      const chunk = wrapped.slice(i, i + room);
      drawLines(doc, chunk, x, y, line.pt, { bold: line.bold, color: line.color, widthMm: width });
      y += chunk.length * lh;
      i += chunk.length;
    }
    y += line.gapAfter ?? 0;
  }
  return y;
}
